<template>
  <div class="ibox">
    <a class="ibox-results-collapse visible-xs" role="button" data-toggle="collapse" href=".ibox-results-filters-collapse-box" aria-expanded="false" aria-controls="ibox-results-filters-collapse-box">
      Show Filters <i class="fa fa-bars"></i>
    </a>
    <div class="ibox-results-filters-collapse-box collapse in">
      <div class="ibox-results-filters">
        <div class="row">
          <div class="col-sm-6 col-lg-4">
            <div class="form-group">
              <label class="control-label">Keywords</label>
              <input type="text" class="form-control" placeholder="..." @input="updateKeywords">
            </div>
          </div>
          <div class="col-sm-6 col-lg-4">
            <div class="form-group">
              <label class="control-label">Show Archived</label>
              <div class="checkbox">
                <input id="check-archive" class="styled" type="checkbox" v-model="archived">
                <label for="check-archive">
                  Yes
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="ibox-content">
      <ul class="list-detail list-detail-user">
        <account-users-list-item
          v-for="user in accountUsers"
          :key="user.id"
          :account-user="user">
        </account-users-list-item>

        <li class="text-center text-muted" v-show="haveNoAccountUsers">
          <strong>There are no account users.</strong>
        </li>
      </ul>

      <lazy-loading v-show="haveAccountUsers" :reached-bottom="proxyGetAccountUsersForNextPage" :filters="filters" ref="lazyLoading">
        <strong slot="finished" class="text-center text-muted">There are no more account users.</strong>
      </lazy-loading>

      <account-users-modal></account-users-modal>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapState, mapGetters, mapMutations } from 'vuex'
  import { ADD_ACCOUNT_USER } from 'vx/mutations'
  import { existingUser } from 'mixins/user'
  import { debounce, isEqual } from 'lodash'
  import { LazyLoading } from 'components/misc'
  import AccountUsersListItem from './AccountUsersListItem'
  import AccountUsersModal from './AccountUsersModal'

  export default {
    mixins: [existingUser],

    components: {
      AccountUsersListItem,
      AccountUsersModal,
      LazyLoading
    },

    data () {
      return {
        keywords: null,
        archived: false
      }
    },

    created () {
      this.attemptToLoadExistingUser()
        .then(this.getExistingUsersAccountUsers)
        .catch(this.redirectToDashboard)

      this.$bus.$on('accountUserUpdated', this[ADD_ACCOUNT_USER])
    },

    beforeDestroy () {
      this.$bus.$off('accountUserUpdated', this[ADD_ACCOUNT_USER])
    },

    watch: {
      /**
       * Watch the filters for changes, trigger a refresh on the account users and reset
       * the lazy loading.
       */
      filters: {
        handler (newFilters, oldFilters) {
          if (isEqual(newFilters, oldFilters)) {
            return
          }

          // Reset the lazy loading component so that it will attempt to load
          // more account users as we scroll again.
          const resetLazyLoading = () => (this.$refs.lazyLoading.$emit('reset'))

          this.getAccountUsers({ userId: this.user.id, payload: this.filters }).then(resetLazyLoading)
        },
        deep: true
      },

      /**
       * Watch the archived checkbox and reset the lazy loading component when it changes.
       *
       * @return void
       */
      archived () {
        this.$refs.lazyLoading.$emit('reset')
      },

      /**
       * When the route changes trigger an attempt to load an existing
       * user.
       */
      '$route' () {
        this.attemptToLoadExistingUser()
          .then(this.getExistingUsersAccountUsers)
          .catch(this.redirectToDashboard)
      }
    },

    computed: {
      ...mapState('user/accountUsers', ['accountUsers', 'accountUser']),
      ...mapGetters('user/accountUsers', ['accountUserDecorated', 'haveAccountUser']),

      /**
       * Build filter object.
       *
       * @return object
       */
      filters () {
        return {
          keywords: this.keywords,
          archived: this.archived
        }
      },

      /**
       * Determine if we do not have any account users.
       *
       * @return boolean
       */
      haveNoAccountUsers () {
        return this.accountUsers.length === 0
      },

      /**
       * Determine if we have any account users.
       *
       * @return boolean
       */
      haveAccountUsers () {
        return this.accountUsers.length > 0
      }
    },

    methods: {
      ...mapActions('user/accountUsers', [
        'getAccountUsers',
        'getAccountUsersForNextPage'
      ]),
      ...mapMutations('user/accountUsers', [ADD_ACCOUNT_USER]),

      /**
       * Redirect to the dashboard.
       *
       * @return void
       */
      redirectToDashboard () {
        this.$router.push({ name: 'dashboard' })
      },

      /**
       * Get the logged in users/user being edited account users.
       *
       * @return Promise
       */
      getExistingUsersAccountUsers () {
        return this.getAccountUsers({ userId: this.user.id, payload: this.filters })
      },

      /**
       * Proxy method to add in the "userId" and the "payload".
       *
       * @return Promise
       */
      proxyGetAccountUsers () {
        return this.getAccountUsers({ userId: this.user.id, payload: this.filters })
      },

      /**
       * Proxy method to add in the "userId" to the payload.
       *
       * @param object payload
       *
       * @return Promise
       */
      proxyGetAccountUsersForNextPage (payload) {
        return this.getAccountUsersForNextPage({ userId: this.user.id, payload })
      },

      /**
       * Update the keywords filter.
       *
       * @param object event
       *
       * @return Function
       */
      updateKeywords: debounce(function (event) {
        this.keywords = event.target.value
      }, 800)
    }
  }
</script>



// WEBPACK FOOTER //
// AccountUsersList.vue?e4ee67c8